import React, { useEffect, useState } from "react";
import { Box, Paper, Toolbar, Typography, Button, Snackbar } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import axios from "axios";

export default function RoomCapacitySummary() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Helper
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch and aggregate room capacity data (SRS)
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const clientId = sessionStorage.getItem("client_id") || "1";
        const url = `${API_BASE_URL}/api/exam-rooms/${clientId}`;
        const response = await axios.get(url, { headers: getAuthHeaders() });
        const data = Array.isArray(response.data) ? response.data : [];
        // SRS: Aggregate by room_type, show total rooms, total capacity, active/inactive count
        const summary = {};
        data.forEach((item) => {
          const type = item.room_type || "UNKNOWN";
          if (!summary[type]) {
            summary[type] = {
              room_type: type,
              total_rooms: 0,
              total_capacity: 0,
              active: 0,
              inactive: 0,
            };
          }
          summary[type].total_rooms += 1;
          summary[type].total_capacity += Number(item.room_strength) || 0;
          if (item.status === "ACTIVE") summary[type].active += 1;
          else summary[type].inactive += 1;
        });
        setRows(Object.values(summary));
      } catch (error) {
        console.error("Error fetching exam room data:", error);
        showSnackbar("Error fetching exam room data", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Columns for DataGrid
  const columns = [
    { field: "room_type", headerName: "Room Type", flex: 1.2, minWidth: 160 },
    { field: "total_rooms", headerName: "Total Rooms", flex: 1, minWidth: 120, type: "number" },
    {
      field: "total_capacity",
      headerName: "Total Capacity",
      flex: 1,
      minWidth: 120,
      type: "number",
    },
    { field: "active", headerName: "Active Rooms", flex: 1, minWidth: 120, type: "number" },
    { field: "inactive", headerName: "Inactive Rooms", flex: 1, minWidth: 120, type: "number" },
  ];

  // Export CSV
  const handleDownloadCsvReport = () => {
    try {
      const csvHeaders = [
        "Room Type",
        "Total Rooms",
        "Total Capacity",
        "Active Rooms",
        "Inactive Rooms",
      ];
      const csvData = rows.map((r) => [
        r.room_type,
        r.total_rooms,
        r.total_capacity,
        r.active,
        r.inactive,
      ]);
      const csvContent = [
        csvHeaders.join(","),
        ...csvData.map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "room_capacity_summary_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error generating CSV:", error);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  // Export PDF (simple table)
  const handleDownloadPdfReport = () => {
    try {
      const html = `
        <html><head><title>Room Capacity Summary PDF</title></head><body>
        <h2>Room Capacity Summary Report</h2>
        <table border="1" cellpadding="6" style="border-collapse:collapse;">
          <thead>
            <tr>
              <th>Room Type</th>
              <th>Total Rooms</th>
              <th>Total Capacity</th>
              <th>Active Rooms</th>
              <th>Inactive Rooms</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (row) =>
                  `<tr>
                <td>${row.room_type}</td>
                <td>${row.total_rooms}</td>
                <td>${row.total_capacity}</td>
                <td>${row.active}</td>
                <td>${row.inactive}</td>
              </tr>`
              )
              .join("")}
          </tbody>
        </table>
        </body></html>
      `;
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      win.print();
      showSnackbar("PDF export (print preview) opened", "info");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showSnackbar("Error downloading PDF", "error");
    }
  };

  // UI
  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ROOM CAPACITY SUMMARY REPORT
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: "#4caf50",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            onClick={handleDownloadCsvReport}
          >
            DOWNLOAD CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            sx={{
              backgroundColor: "#e53935",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#c62828" },
            }}
            onClick={handleDownloadPdfReport}
          >
            DOWNLOAD PDF
          </Button>
        </Toolbar>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={rows.map((r, i) => ({ ...r, id: i }))}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>
      <Footer />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
